/*
 * pt26-collection-filters.js — <pt26-collection-filters>, die Filterleiste
 * über dem Produktgrid der Collection Page (snippets/pt26-collection-filters.liquid).
 *
 * Ab 741 wendet jede Änderung sofort an: URL aus dem Formular, replaceState,
 * dann baut die Section Rendering API die ganze Section neu auf. Dabei
 * verbinden sich Grid und Leiste neu, Endless Scroll und Promotion-Platzierung
 * starten von vorn. Unter 741 öffnen die Auslöser ein Bottom Sheet (<dialog>),
 * in das das vorhandene Panel verschoben wird. Dort wird nur vorgemerkt,
 * erst „Bestätigen" oder „Zurücksetzen" schließt und wendet über dasselbe
 * apply() an. Ohne JavaScript bleibt das GET-Formular mit Submit-Button.
 *
 * Die Leiste scrollt nativ. Ab 741 blättern Pfeile sie weiter, sichtbar nur in
 * die Richtung, in die noch Filter verdeckt sind. Embla scheidet hier aus: seine
 * Verschiebung per transform würde die Dropdowns im Viewport abschneiden.
 * Lazy geladen von pt26-core.js; beim Import läuft keine Arbeit.
 */

const FILTER_SELECTOR = '[data-pt26-filter]';
const PANEL_SELECTOR = '[data-pt26-filter-panel]';
const PRICE_SELECTOR = '[data-pt26-price]';
const SORT_SELECTOR = '[data-pt26-sort]';
const INPUT_SELECTOR = 'input[type="checkbox"], input[type="radio"], input[type="range"]';
const ACTION_BUTTONS_SELECTOR = '[data-pt26-filter-reset] button, [data-pt26-filter-confirm] button';

const DESKTOP_QUERY = '(min-width: 741px)';

// Eine sofort eingefügte Live-Region lesen Screenreader oft nicht vor. Kurz warten, dann befüllen.
const ANNOUNCE_DELAY_MS = 150;

// Rundungsspielraum beim Vergleich von Scroll- und Kantenwerten, sonst bleibt am Ende ein Pfeil stehen.
const SCROLL_TOLERANCE_PX = 1;

class Pt26CollectionFilters extends HTMLElement {
  constructor() {
    super();

    this.form = null;
    this.bar = null;
    this.status = null;
    this.sheet = null;
    this.sheetTitle = null;
    this.sheetBody = null;
    this.prevControl = null;
    this.nextControl = null;
    this.barResizeObserver = null;
    this.desktopMedia = null;
    this.sheetSession = null;
    this.abortController = null;
    this.announceHandle = null;

    this.handleChange = this.handleChange.bind(this);
    this.handleInput = this.handleInput.bind(this);
    this.handleClick = this.handleClick.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleToggle = this.handleToggle.bind(this);
    this.handleKeydown = this.handleKeydown.bind(this);
    this.handleDocumentClick = this.handleDocumentClick.bind(this);
    this.handleSheetClick = this.handleSheetClick.bind(this);
    this.handleSheetClose = this.handleSheetClose.bind(this);
    this.handleViewportChange = this.handleViewportChange.bind(this);
    this.updateBarControls = this.updateBarControls.bind(this);
  }

  connectedCallback() {
    this.form = this.querySelector('[data-pt26-collection-filters-form]');
    this.bar = this.querySelector('[data-pt26-collection-filters-bar]');
    this.status = this.querySelector('[data-pt26-collection-filters-status]');
    this.sheet = this.querySelector('[data-pt26-filter-sheet]');
    this.sheetTitle = this.querySelector('[data-pt26-filter-sheet-title]');
    this.sheetBody = this.querySelector('[data-pt26-filter-sheet-body]');
    this.prevControl = this.querySelector('[data-pt26-filter-bar-prev]');
    this.nextControl = this.querySelector('[data-pt26-filter-bar-next]');

    if (!this.form) {
      return;
    }

    this.querySelectorAll('[data-pt26-filter-actions]').forEach((actions) => {
      actions.hidden = false;
    });

    if (this.bar) {
      this.bar.addEventListener('scroll', this.updateBarControls, { passive: true });
      // Eine andere Fensterbreite kann den Überlauf entstehen oder verschwinden lassen.
      this.barResizeObserver = new ResizeObserver(this.updateBarControls);
      this.barResizeObserver.observe(this.bar);
      this.updateBarControls();
    }

    this.querySelectorAll(PRICE_SELECTOR).forEach((price) => {
      this.syncPrice(price);
    });

    this.desktopMedia = window.matchMedia(DESKTOP_QUERY);
    this.desktopMedia.addEventListener('change', this.handleViewportChange);

    this.form.addEventListener('change', this.handleChange);
    this.form.addEventListener('input', this.handleInput);
    this.form.addEventListener('submit', this.handleSubmit);
    this.addEventListener('click', this.handleClick);
    this.addEventListener('keydown', this.handleKeydown);
    // toggle steigt nicht auf, deshalb in der Capture-Phase.
    this.addEventListener('toggle', this.handleToggle, true);
    document.addEventListener('click', this.handleDocumentClick);

    if (this.sheet) {
      this.sheet.addEventListener('click', this.handleSheetClick);
      this.sheet.addEventListener('close', this.handleSheetClose);
    }
  }

  disconnectedCallback() {
    if (this.form) {
      this.form.removeEventListener('change', this.handleChange);
      this.form.removeEventListener('input', this.handleInput);
      this.form.removeEventListener('submit', this.handleSubmit);
    }

    this.removeEventListener('click', this.handleClick);
    this.removeEventListener('keydown', this.handleKeydown);
    this.removeEventListener('toggle', this.handleToggle, true);
    document.removeEventListener('click', this.handleDocumentClick);

    if (this.bar) {
      this.bar.removeEventListener('scroll', this.updateBarControls);
    }

    if (this.barResizeObserver) {
      this.barResizeObserver.disconnect();
      this.barResizeObserver = null;
    }

    if (this.desktopMedia) {
      this.desktopMedia.removeEventListener('change', this.handleViewportChange);
      this.desktopMedia = null;
    }

    if (this.sheet) {
      this.sheet.removeEventListener('click', this.handleSheetClick);
      this.sheet.removeEventListener('close', this.handleSheetClose);

      if (this.sheet.open) {
        this.sheet.close();
      }
    }

    if (this.sheetSession) {
      this.sheetSession = null;
      this.unlockScroll();
    }

    window.clearTimeout(this.announceHandle);
    this.announceHandle = null;

    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }
  }

  /**
   * Öffentliche Anwenden-Schnittstelle für Filter, Sortierung, Preis und das
   * Bottom Sheet. Ohne Argument gilt der Stand des Formulars.
   *
   * @param {string} [url] - Seiten-URL mit Filtern, ohne section_id.
   * @returns {Promise<void>}
   */
  async apply(url = this.buildFormUrl()) {
    const pageUrl = new URL(url, window.location.origin);
    pageUrl.searchParams.delete('page');
    pageUrl.searchParams.delete('section_id');

    history.replaceState(history.state, '', pageUrl.pathname + pageUrl.search);

    if (this.abortController) {
      this.abortController.abort();
    }

    const controller = new AbortController();
    this.abortController = controller;

    const requestUrl = new URL(pageUrl);
    requestUrl.searchParams.set('section_id', this.getAttribute('data-section-id'));

    try {
      const response = await fetch(requestUrl.toString(), {
        credentials: 'same-origin',
        signal: controller.signal,
      });

      if (!response.ok) {
        throw new Error('HTTP ' + response.status);
      }

      const html = await response.text();

      if (this.abortController !== controller) {
        return;
      }

      // Nach dem Ersetzen ist dieses Element getrennt, disconnectedCallback räumt den Controller ab.
      this.replaceSection(html);
    } catch (error) {
      if (error.name === 'AbortError' || this.abortController !== controller) {
        return;
      }

      // Die Filter-URL steht schon in der Adresszeile. Ein normaler Seitenaufruf zeigt dann das richtige Ergebnis.
      console.error('[pt26] collection-filters: filtered section failed to load', error);
      window.location.assign(pageUrl.toString());
    }
  }

  /**
   * Ein Preisregler am Anschlag filtert nicht, deshalb fehlt er in der URL.
   *
   * @returns {string}
   */
  buildFormUrl() {
    const formData = new FormData(this.form);

    this.form.querySelectorAll('input[type="range"]').forEach((range) => {
      if (this.isRangeAtBound(range)) {
        formData.delete(range.name);
      }
    });

    const formUrl = new URL(this.form.action, window.location.origin);
    formUrl.search = new URLSearchParams(formData).toString();
    return formUrl.toString();
  }

  /**
   * Ersetzt den Inhalt der Section. Offenes Dropdown, Fokus und Scrollstand
   * werden vorher gemerkt und an die neue Leiste übergeben, weil dieses
   * Element beim Ersetzen verschwindet.
   *
   * @param {string} html - Antwort der Section Rendering API.
   */
  replaceSection(html) {
    const sectionId = this.getAttribute('data-section-id');
    const sectionElement = this.closest('.shopify-section');
    const responseDocument = new DOMParser().parseFromString(html, 'text/html');
    const responseSection = responseDocument.getElementById('shopify-section-' + sectionId);

    if (!sectionElement || !responseSection) {
      throw new Error('section shopify-section-' + sectionId + ' not found');
    }

    const state = this.captureState();

    sectionElement.innerHTML = responseSection.innerHTML;

    const nextFilters = sectionElement.querySelector('pt26-collection-filters');

    if (nextFilters && typeof nextFilters.restoreState === 'function') {
      nextFilters.restoreState(state);
    }
  }

  /**
   * @returns {{openFilter: string, focusSelector: string, barScrollLeft: number, optionsScrollTop: number}}
   */
  captureState() {
    const openFilter = this.querySelector(FILTER_SELECTOR + '[open]');
    const options = openFilter ? openFilter.querySelector('[data-pt26-filter-options]') : null;

    return {
      openFilter: openFilter ? openFilter.getAttribute('data-pt26-filter') : '',
      focusSelector: this.describeFocus(document.activeElement),
      barScrollLeft: this.bar ? this.bar.scrollLeft : 0,
      optionsScrollTop: options ? options.scrollTop : 0,
    };
  }

  /**
   * Beschreibt das fokussierte Element so, dass es sich im neuen Markup
   * wiederfinden lässt: Checkbox und Radio über Name und Wert, Preisregler
   * über den Namen, Buttons und Auslöser über ihren Filter.
   *
   * @param {Element | null} element
   * @returns {string} Selektor innerhalb dieses Elements, leer ohne Fokus hier.
   */
  describeFocus(element) {
    if (!element || !this.contains(element)) {
      return '';
    }

    if (element.matches('input[type="range"]')) {
      return 'input[name="' + CSS.escape(element.name) + '"]';
    }

    if (element.matches(INPUT_SELECTOR)) {
      return 'input[name="' + CSS.escape(element.name) + '"][value="' + CSS.escape(element.value) + '"]';
    }

    // Nach „Filter zurücksetzen" gibt es den Link nicht mehr. Der Fokus geht dann auf den ersten Filter der Leiste.
    if (element.closest('[data-pt26-filter-reset-all]')) {
      return FILTER_SELECTOR + ' > summary';
    }

    const filter = element.closest(FILTER_SELECTOR);

    if (!filter) {
      return '';
    }

    const filterSelector = '[data-pt26-filter="' + CSS.escape(filter.getAttribute('data-pt26-filter')) + '"]';

    if (element.closest('[data-pt26-filter-reset]')) {
      return filterSelector + ' [data-pt26-filter-reset] button';
    }

    if (element.closest('[data-pt26-filter-confirm]')) {
      return filterSelector + ' [data-pt26-filter-confirm] button';
    }

    return filterSelector + ' > summary';
  }

  /**
   * Wird von der Vorgänger-Instanz direkt nach dem Ersetzen aufgerufen.
   *
   * @param {{openFilter: string, focusSelector: string, barScrollLeft: number, optionsScrollTop: number}} state
   */
  restoreState(state) {
    if (this.bar) {
      this.bar.scrollLeft = state.barScrollLeft;
    }

    const openFilter = state.openFilter ? this.findFilter(state.openFilter) : null;

    if (openFilter) {
      openFilter.open = true;
      this.setExpanded(openFilter, true);

      const options = openFilter.querySelector('[data-pt26-filter-options]');

      if (options) {
        options.scrollTop = state.optionsScrollTop;
      }
    }

    let focusTarget = state.focusSelector ? this.querySelector(state.focusSelector) : null;

    // Nach „Zurücksetzen" ist der Button deaktiviert. Dann geht der Fokus auf den ersten Wert desselben Filters.
    if (focusTarget && focusTarget.disabled && openFilter) {
      focusTarget = openFilter.querySelector(INPUT_SELECTOR);
    }

    if (focusTarget) {
      focusTarget.focus({ preventScroll: true });
    }

    this.announceResults();
  }

  announceResults() {
    if (!this.status) {
      return;
    }

    this.announceHandle = window.setTimeout(() => {
      this.status.textContent = this.getAttribute('data-results-text') || '';
    }, ANNOUNCE_DELAY_MS);
  }

  /**
   * Preisregler melden change erst beim Loslassen. Im Sheet wird nur
   * vorgemerkt, sonst sofort angewendet.
   *
   * @param {Event} event
   */
  handleChange(event) {
    const input = event.target;

    if (!input.matches(INPUT_SELECTOR)) {
      return;
    }

    const panel = input.closest(PANEL_SELECTOR);

    if (panel) {
      this.syncEntries(panel);
      this.syncActions(panel);
    } else {
      this.syncOption(input);
    }

    if (this.sheetSession) {
      this.syncSheetActions();
      return;
    }

    this.apply();
  }

  /**
   * Hält die Beträge beim Ziehen aktuell und verhindert, dass sich die
   * Regler überholen (wie HmFilterRange in assets/theme.js).
   *
   * @param {Event} event
   */
  handleInput(event) {
    const range = event.target;

    if (!range.matches('input[type="range"]')) {
      return;
    }

    const price = range.closest(PRICE_SELECTOR);

    if (!price) {
      return;
    }

    this.keepRangesApart(price, range);
    this.syncPrice(price);
  }

  /**
   * @param {SubmitEvent} event
   */
  handleSubmit(event) {
    event.preventDefault();
    this.apply();
  }

  /**
   * @param {MouseEvent} event
   */
  handleClick(event) {
    const target = event.target;

    if (target.closest('[data-pt26-filter-bar-prev] button')) {
      this.scrollBarPage(-1);
      return;
    }

    if (target.closest('[data-pt26-filter-bar-next] button')) {
      this.scrollBarPage(1);
      return;
    }

    const resetAllLink = target.closest('[data-pt26-filter-reset-all]');

    // Der Link führt ohne JavaScript auf die ungefilterte Seite. Hier lädt stattdessen nur die Section neu.
    if (resetAllLink) {
      event.preventDefault();
      this.apply(resetAllLink.href);
      return;
    }

    const trigger = target.closest(FILTER_SELECTOR + ' > summary');

    // Unter 741 öffnet der Auslöser das Sheet statt des Dropdowns.
    if (trigger && !this.isDesktop() && this.sheet) {
      event.preventDefault();
      this.openSheet(trigger.parentElement, trigger);
      return;
    }

    if (target.closest('[data-pt26-filter-sheet-close]')) {
      this.closeSheet(false);
      return;
    }

    const resetButton = target.closest('[data-pt26-filter-reset] button');
    const confirmButton = target.closest('[data-pt26-filter-confirm] button');

    if (!resetButton && !confirmButton) {
      return;
    }

    // Beide Buttons schließen Sheet und Dropdown (Review Lorenz 04.10.2026). „Zurücksetzen" wendet das Leeren dabei gleich an.
    if (this.sheetSession) {
      if (resetButton) {
        this.resetEntries(this.sheetBody);
      }

      this.closeSheet(true);
      return;
    }

    const panel = target.closest(PANEL_SELECTOR);
    const filter = panel ? panel.closest(FILTER_SELECTOR) : null;

    if (!panel || !filter) {
      return;
    }

    if (resetButton) {
      this.resetEntries(panel);
      this.syncActions(panel);
      // Vor apply() schließen, sonst merkt sich captureState() das Dropdown als offen und die neue Leiste öffnet es wieder.
      this.closeFilter(filter, true);
      this.apply();
      return;
    }

    // Die Auswahl ist schon angewendet, „Bestätigen" schließt nur (wie facet-mobile-filters.liquid:190).
    this.closeFilter(filter, true);
  }

  /**
   * Hält genau ein Dropdown offen und aria-expanded am Auslöser aktuell.
   *
   * @param {Event} event
   */
  handleToggle(event) {
    const filter = event.target;

    if (!(filter instanceof HTMLDetailsElement) || !filter.matches(FILTER_SELECTOR)) {
      return;
    }

    this.setExpanded(filter, filter.open);

    if (!filter.open) {
      return;
    }

    this.querySelectorAll(FILTER_SELECTOR + '[open]').forEach((otherFilter) => {
      if (otherFilter !== filter) {
        this.closeFilter(otherFilter, false);
      }
    });
  }

  /**
   * @param {KeyboardEvent} event
   */
  handleKeydown(event) {
    if (event.key !== 'Escape') {
      return;
    }

    const openFilter = this.querySelector(FILTER_SELECTOR + '[open]');

    if (openFilter) {
      event.preventDefault();
      this.closeFilter(openFilter, true);
    }
  }

  /**
   * Klick außerhalb schließt. Den Fokus holt der Auslöser nur zurück, wenn er
   * nicht schon auf etwas anderes gewechselt ist.
   *
   * @param {MouseEvent} event
   */
  handleDocumentClick(event) {
    const openFilter = this.querySelector(FILTER_SELECTOR + '[open]');

    if (!openFilter || openFilter.contains(event.target)) {
      return;
    }

    // Ein Klick auf einen anderen Auslöser der Leiste öffnet dessen Dropdown, der Fokus bleibt dort.
    const clickedInsideBar = this.contains(event.target);
    const activeElement = document.activeElement;
    const focusIsLost = !activeElement || activeElement === document.body || openFilter.contains(activeElement);

    this.closeFilter(openFilter, focusIsLost && !clickedInsideBar);
  }

  /**
   * Der Inhalt füllt das Sheet ganz aus. Trifft ein Klick den Dialog selbst,
   * lag er auf dem Overlay.
   *
   * @param {MouseEvent} event
   */
  handleSheetClick(event) {
    if (event.target === this.sheet) {
      this.closeSheet(false);
    }
  }

  /**
   * Esc schließt den Dialog nativ. Das zählt wie Schließen ohne Bestätigen.
   */
  handleSheetClose() {
    this.closeSheet(false);
  }

  /**
   * Wer über 741 hinaus dreht, landet in der Dropdown-Ansicht, die Vormerkung verfällt.
   */
  handleViewportChange() {
    if (this.isDesktop() && this.sheetSession) {
      this.closeSheet(false);
    }
  }

  /**
   * @returns {boolean}
   */
  isDesktop() {
    return this.desktopMedia ? this.desktopMedia.matches : window.matchMedia(DESKTOP_QUERY).matches;
  }

  /**
   * Öffnet das Bottom Sheet für einen Filter. Das Panel wird verschoben,
   * nicht kopiert. Ein Platzhalter merkt sich, wohin es zurückgehört.
   *
   * @param {HTMLDetailsElement} filter
   * @param {HTMLElement} trigger - Bekommt beim Schließen den Fokus zurück.
   */
  openSheet(filter, trigger) {
    if (!this.sheet || !this.sheetBody || this.sheetSession) {
      return;
    }

    const panel = filter.querySelector(PANEL_SELECTOR);

    if (!panel) {
      return;
    }

    const placeholder = document.createComment('');
    panel.before(placeholder);
    this.sheetBody.append(panel);

    this.sheetSession = {
      filter,
      trigger,
      panel,
      placeholder,
      snapshot: this.takeSnapshot(this.sheetBody),
    };

    this.sheetTitle.textContent = filter.getAttribute('data-pt26-filter-label') || '';

    this.syncSheetActions();
    this.sheet.showModal();
    this.lockScroll();
    this.setExpanded(filter, true);
  }

  /**
   * Schließt das Sheet. Ohne Bestätigen wird die Vormerkung verworfen, mit
   * Bestätigen geht das Panel zurück an seinen Platz und apply() wendet an.
   *
   * @param {boolean} shouldApply
   */
  closeSheet(shouldApply) {
    const session = this.sheetSession;

    if (!session) {
      return;
    }

    // Vor close(), damit das close-Ereignis diesen Weg nicht ein zweites Mal geht.
    this.sheetSession = null;

    if (!shouldApply) {
      this.restoreSnapshot(session.snapshot);
    }

    session.placeholder.replaceWith(session.panel);

    if (this.sheet.open) {
      this.sheet.close();
    }

    this.unlockScroll();
    this.setExpanded(session.filter, false);
    session.trigger.focus();

    if (shouldApply) {
      this.apply();
    }
  }

  /**
   * Blendet die Pfeile nur ein, wenn in ihre Richtung noch Filter verdeckt
   * sind. Ohne Überlauf bleiben beide versteckt.
   */
  updateBarControls() {
    if (!this.bar) {
      return;
    }

    const maxScrollLeft = this.bar.scrollWidth - this.bar.clientWidth;
    const canScrollPrev = this.bar.scrollLeft > SCROLL_TOLERANCE_PX;
    const canScrollNext = this.bar.scrollLeft < maxScrollLeft - SCROLL_TOLERANCE_PX;

    if (this.prevControl) {
      this.prevControl.hidden = !canScrollPrev;
    }

    if (this.nextControl) {
      this.nextControl.hidden = !canScrollNext;
    }
  }

  /**
   * Blättert wie die Kreis-Navigation um so viele Filter, wie in den Container
   * passen: vorwärts beginnt die Leiste mit dem ersten angeschnittenen Filter,
   * rückwärts endet sie mit dem Filter vor dem bisher ersten.
   *
   * @param {number} direction - 1 vorwärts, -1 rückwärts.
   */
  scrollBarPage(direction) {
    if (!this.bar) {
      return;
    }

    // Maßstab ist der Container, nicht die Leiste: sie läuft rechts bis zum Bildschirmrand weiter.
    const containerRect = this.getBoundingClientRect();
    const items = Array.from(this.bar.children);
    let scrollDistance = 0;

    if (direction > 0) {
      const firstCutItem = items.find(
        (item) => item.getBoundingClientRect().right > containerRect.right + SCROLL_TOLERANCE_PX
      );
      scrollDistance = firstCutItem ? firstCutItem.getBoundingClientRect().left - containerRect.left : 0;
    } else {
      const firstVisibleIndex = items.findIndex(
        (item) => item.getBoundingClientRect().left >= containerRect.left - SCROLL_TOLERANCE_PX
      );
      const pageEnd = firstVisibleIndex > 0 ? items[firstVisibleIndex - 1].getBoundingClientRect().right : containerRect.left;
      const pageStartItem = items.find(
        (item) => item.getBoundingClientRect().left >= pageEnd - containerRect.width - SCROLL_TOLERANCE_PX
      );
      scrollDistance = pageStartItem ? pageStartItem.getBoundingClientRect().left - containerRect.left : -this.bar.scrollLeft;
    }

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.bar.scrollBy({
      left: scrollDistance,
      behavior: prefersReducedMotion ? 'auto' : 'smooth',
    });
  }

  /**
   * @param {Element} scope
   * @returns {{input: HTMLInputElement, checked: boolean, value: string}[]}
   */
  takeSnapshot(scope) {
    return Array.from(scope.querySelectorAll(INPUT_SELECTOR)).map((input) => ({
      input,
      checked: input.checked,
      value: input.value,
    }));
  }

  /**
   * @param {{input: HTMLInputElement, checked: boolean, value: string}[]} snapshot
   */
  restoreSnapshot(snapshot) {
    snapshot.forEach(({ input, checked, value }) => {
      input.checked = checked;
      input.value = value;
    });

    this.syncEntries(this.sheetBody);
    this.sheetBody.querySelectorAll(PANEL_SELECTOR).forEach((panel) => {
      this.syncActions(panel);
    });
  }

  /**
   * @param {{input: HTMLInputElement, checked: boolean, value: string}[]} snapshot
   * @returns {boolean}
   */
  hasChangedSince(snapshot) {
    return snapshot.some(({ input, checked, value }) => input.checked !== checked || input.value !== value);
  }

  /**
   * Leert die Auswahl: Checkboxen aus, Preisregler an den Anschlag,
   * Sortierung zurück auf die Vorgabe der Collection.
   *
   * @param {Element} scope
   */
  resetEntries(scope) {
    scope.querySelectorAll('input[type="checkbox"]').forEach((input) => {
      input.checked = false;
    });

    scope.querySelectorAll('input[type="range"]').forEach((range) => {
      range.value = range.getAttribute('data-pt26-price-bound') === 'min' ? range.min : range.max;
    });

    scope.querySelectorAll(SORT_SELECTOR).forEach((sort) => {
      const defaultValue = sort.getAttribute('data-default-value');

      sort.querySelectorAll('input[type="radio"]').forEach((radio) => {
        radio.checked = radio.value === defaultValue;
      });
    });

    this.syncEntries(scope);
  }

  /**
   * Gibt es in diesem Bereich eine Auswahl, die filtert oder anders sortiert?
   *
   * @param {Element} scope
   * @returns {boolean}
   */
  hasSelection(scope) {
    if (scope.querySelector('input[type="checkbox"]:checked')) {
      return true;
    }

    const ranges = Array.from(scope.querySelectorAll('input[type="range"]'));

    if (ranges.some((range) => !this.isRangeAtBound(range))) {
      return true;
    }

    return Array.from(scope.querySelectorAll(SORT_SELECTOR)).some((sort) => {
      const checkedRadio = sort.querySelector('input[type="radio"]:checked');
      return checkedRadio !== null && checkedRadio.value !== sort.getAttribute('data-default-value');
    });
  }

  /**
   * @param {HTMLInputElement} range
   * @returns {boolean}
   */
  isRangeAtBound(range) {
    const value = parseFloat(range.value);

    if (range.getAttribute('data-pt26-price-bound') === 'min') {
      return value <= parseFloat(range.min);
    }

    return value >= parseFloat(range.max);
  }

  /**
   * @param {Element} price
   * @param {HTMLInputElement} movedRange
   */
  keepRangesApart(price, movedRange) {
    const minRange = price.querySelector('[data-pt26-price-bound="min"]');
    const maxRange = price.querySelector('[data-pt26-price-bound="max"]');

    if (!minRange || !maxRange) {
      return;
    }

    const step = parseFloat(movedRange.step) || 1;

    if (movedRange === minRange) {
      minRange.value = String(Math.min(parseFloat(minRange.value), parseFloat(maxRange.value) - step));
    } else {
      maxRange.value = String(Math.max(parseFloat(maxRange.value), parseFloat(minRange.value) + step));
    }
  }

  /**
   * Schreibt die Beträge neben die Regler und als aria-valuetext an sie,
   * damit der Screenreader einen Preis statt einer nackten Zahl vorliest.
   *
   * @param {Element} price
   */
  syncPrice(price) {
    const formatter = this.createPriceFormatter(price);

    price.querySelectorAll('input[type="range"]').forEach((range) => {
      const amount = formatter(parseFloat(range.value));
      const output = range.closest('label') ? range.closest('label').querySelector('[data-pt26-price-output]') : null;

      range.setAttribute('aria-valuetext', amount);

      if (output) {
        output.textContent = amount;
      }
    });
  }

  /**
   * @param {Element} price - Trägt data-currency und data-locale.
   * @returns {(amount: number) => string}
   */
  createPriceFormatter(price) {
    try {
      const numberFormat = new Intl.NumberFormat(price.getAttribute('data-locale') || undefined, {
        style: 'currency',
        currency: price.getAttribute('data-currency'),
      });

      return (amount) => numberFormat.format(amount);
    } catch (error) {
      // Ohne gültige Währung lieber die nackte Zahl als eine falsch formatierte.
      return (amount) => String(amount);
    }
  }

  /**
   * @param {Element} scope
   */
  syncEntries(scope) {
    scope.querySelectorAll('.pt26-collection-filters__option input').forEach((input) => {
      this.syncOption(input);
    });

    scope.querySelectorAll(PRICE_SELECTOR).forEach((price) => {
      this.syncPrice(price);
    });
  }

  /**
   * @param {HTMLDetailsElement} filter
   * @param {boolean} returnFocus
   */
  closeFilter(filter, returnFocus) {
    filter.open = false;
    this.setExpanded(filter, false);

    if (returnFocus) {
      const trigger = filter.querySelector(':scope > summary');

      if (trigger) {
        trigger.focus();
      }
    }
  }

  /**
   * @param {HTMLDetailsElement} filter
   * @param {boolean} isExpanded
   */
  setExpanded(filter, isExpanded) {
    const trigger = filter.querySelector(':scope > summary');

    if (trigger) {
      trigger.setAttribute('aria-expanded', String(isExpanded));
    }
  }

  /**
   * @param {string} paramName
   * @returns {HTMLDetailsElement | null}
   */
  findFilter(paramName) {
    return Array.from(this.querySelectorAll(FILTER_SELECTOR)).find(
      (filter) => filter.getAttribute('data-pt26-filter') === paramName
    ) || null;
  }

  /**
   * Zeigt die Auswahl sofort, bis die neue Section da ist.
   *
   * @param {HTMLInputElement | null} input
   */
  syncOption(input) {
    const option = input ? input.closest('.pt26-collection-filters__option') : null;

    if (!option) {
      return;
    }

    const label = option.querySelector('[data-pt26-filter-option-label]');
    const check = option.querySelector('[data-pt26-filter-option-check]');

    if (label) {
      label.classList.toggle('pt26-body-bold', input.checked);
      label.classList.toggle('pt26-body', !input.checked);
    }

    if (check) {
      check.hidden = !input.checked;
    }
  }

  /**
   * Beide Buttons sind deaktiviert, solange das Panel keine Auswahl hat
   * (Figma 1238:1491 / 1238:6770).
   *
   * @param {Element | null} panel
   */
  syncActions(panel) {
    if (!panel) {
      return;
    }

    const hasSelection = this.hasSelection(panel);

    panel.querySelectorAll(ACTION_BUTTONS_SELECTOR).forEach((button) => {
      button.disabled = !hasSelection;
    });
  }

  /**
   * Im Sheet bleibt „Bestätigen" auch ohne Auswahl aktiv, wenn gerade ein
   * aktiver Filter geleert wurde. Sonst ließe sich das Leeren nie anwenden.
   */
  syncSheetActions() {
    const session = this.sheetSession;

    if (!session) {
      return;
    }

    const hasSelection = this.hasSelection(this.sheetBody);
    const canConfirm = hasSelection || this.hasChangedSince(session.snapshot);

    this.sheetBody.querySelectorAll('[data-pt26-filter-actions]').forEach((actions) => {
      actions.querySelectorAll('[data-pt26-filter-reset] button').forEach((button) => {
        button.disabled = !hasSelection;
      });

      actions.querySelectorAll('[data-pt26-filter-confirm] button').forEach((button) => {
        button.disabled = !canConfirm;
      });
    });
  }

  lockScroll() {
    document.body.style.overflow = 'hidden';
  }

  unlockScroll() {
    document.body.style.overflow = '';
  }
}

if (!customElements.get('pt26-collection-filters')) {
  customElements.define('pt26-collection-filters', Pt26CollectionFilters);
}
